import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  StyledLoadingPlaceholder,
  accentColors,
  legacyColors,
  lighterVariations,
  secondaryColors,
} from '@tryhackme/thm-ui-components';
import styled, { keyframes, css } from 'styled-components';

import { TaskProgressStatus } from './room-footer.types';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const StyledRoomFooterWrapper = styled.div<{
  $showMessage: boolean;
  $isDesktop: boolean;
  $isDarkMode: boolean;
  $isMobile: boolean;
}>`
  display: flex;
  width: 100%;
  min-height: 10rem;
  transition: background 0.25s ease-in-out;
  --success-bg: ${({ $isDarkMode }) =>
    $isDarkMode ? legacyColors.notificationSuccess.dark : lighterVariations.success.lighter};
  --default-bg: ${({ $isDarkMode, theme }) => ($isDarkMode ? theme.colors.secondary.light : secondaryColors.grey[100])};
  background: ${({ $showMessage }) => ($showMessage ? 'var(--success-bg)' : 'var(--default-bg)')};
  align-items: center;
  flex-direction: column;
  gap: ${({ $showMessage }) => ($showMessage ? '2.4rem' : 0)};
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 3.2rem
    ${({ $isDesktop, $isMobile }) => {
      if ($isDesktop) {
        return '7.4rem';
      }
      if ($isMobile) {
        return '1.2rem';
      }
      return '2.4rem';
    }};
`;

export const StyledTasksProgressWrapper = styled.div`
  display: flex;
  gap: 1rem;
  max-width: 100%;
  flex: 1;
`;

export const StyledTaskProgressStatus = styled.div<{
  $status: TaskProgressStatus;
  $isCurrentTask: boolean;
  $isDark: boolean;
  $isDisabled: boolean;
}>`
  border-radius: 5rem;
  height: 1rem;
  width: 6.4rem;
  cursor: ${({ $isDisabled, $isCurrentTask }) => {
    if ($isCurrentTask) {
      return 'auto';
    }
    return $isDisabled ? 'not-allowed' : 'pointer';
  }};
  background: ${({ $status, $isDark }) => {
    if ($status === TaskProgressStatus.COMPLETED) {
      return accentColors.info.light;
    }
    if ($status === TaskProgressStatus.IN_PROGRESS) {
      return accentColors.info.dark;
    }
    return $isDark ? secondaryColors.grey[800] : secondaryColors.grey[400];
  }}};
  &:hover {
    ${({ $status, $isCurrentTask }) =>
      [TaskProgressStatus.COMPLETED, TaskProgressStatus.IN_PROGRESS].includes($status) &&
      !$isCurrentTask &&
      `
        opacity: 60%;
    `}
  }
`;

export const StyledTaskTooltipText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const StyledMessageText = styled.span<{ $isDarkMode: boolean }>`
  font-size: 1.8rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  color: ${({ $isDarkMode, theme }) => ($isDarkMode ? theme.colors.primary.main : accentColors.success.light)};
`;

export const StyledMessageIcon = styled(FontAwesomeIcon)<{ $isDarkMode: boolean }>`
  width: 1.8rem;
  height: 1.8rem;
  color: ${({ $isDarkMode, theme }) => ($isDarkMode ? theme.colors.primary.main : accentColors.success.light)};
`;

export const StyledMessageWrapper = styled.div<{
  $isCorrectMessage: boolean;
}>`
  display: flex;
  flex: 1;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  ${({ $isCorrectMessage }) =>
    $isCorrectMessage &&
    css`
      animation: ${slideUp} 0.5s ease-out forwards, ${fadeOut} 0.5s ease-in forwards 5s;
    `}
`;

export const StyledLoading = styled.div`
  ${StyledLoadingPlaceholder}

  position: absolute;
  inset: 0;
`;

export const StyledShareButton = styled(Button)<{ $isSecondary: boolean; $isDark: boolean }>`
  ${({ $isSecondary, $isDark }) =>
    $isSecondary &&
    `
      background: ${$isDark ? secondaryColors.black : secondaryColors.white};
  `}
`;

export const StyledButtonsWrapper = styled.div<{ $isSmallMobile: boolean }>`
  display: flex;
  flex: 1;
  gap: 1.6rem;
  justify-content: flex-end;
  align-items: center;
  ${({ $isSmallMobile }) =>
    $isSmallMobile &&
    `
      flex-direction: column;
      width: 100%;
      & button {
        width: 100%;
      }
  `}
`;

export const StyledRoomFooterContentWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  width: 100%;
  gap: 2.4rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: column;
    justify-content: center;
  `}
`;
